import { Grid } from '@mui/material';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getAccruingReport } from 'lib/api/reports';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import ReportAccruingTable from './DailyTable';

// Helper functions to replace dayjs functionality
const getStartOfMonth = (date: Date = new Date()): Date => {
  date.setUTCDate(1);
  date.setUTCHours(0, 0, 0, 0);
  return date;
};

const getEndOfMonth = (date: Date = new Date()): Date => {
  date.setUTCDate(31);
  date.setUTCHours(23, 59, 59, 999);
  return date;
};

const isAfter = (date1: Date, date2: Date): boolean => {
  return date1.getTime() > date2.getTime();
};

const AccruingBook: React.FC = () => {
  const [data, setData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [dailyLinesByDocumentId, setDailyLinesByDocumentId] = useState([]);
  const [loading, setLoading] = useState(true);
  const startDateDefault = getStartOfMonth();
  const [startDate, setStartDate] = useState(startDateDefault);
  const endDateDefault = getEndOfMonth();
  const [endDate, setEndDate] = useState(endDateDefault);
  const dispatch = useDispatch();
  const [businessIdentifier, setBusinessIdentifier] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [clientIdentifier, setClientIdentifier] = useState('');
  const [clientName, setClientName] = useState('');
  const [folio, setFolio] = useState('');
  const [product, setProduct] = useState('all');

  const generateQuery = () => {
    let query = '';
    if (startDate) query += `startDate=${startDate}&`;
    if (endDate) query += `endDate=${endDate}&`;
    if (businessIdentifier)
      query += `businessIdentifier=${businessIdentifier}&`;
    if (businessName) query += `businessName=${businessName}&`;
    if (clientName) query += `clientName=${clientName}&`;
    if (clientIdentifier) query += `clientIdentifier=${clientIdentifier}&`;
    if (folio) query += `folio=${folio}&`;
    if (product) query += `productType=${product}&`;
    return query;
  };

  const validateDates = () => {
    if (!startDate || !endDate) {
      showAlert(
        dispatch,
        'warning',
        'Debe seleccionar una fecha de inicio y una fecha de fin',
      );
      return false;
    }
    if (isAfter(new Date(startDate), new Date(endDate))) {
      showAlert(
        dispatch,
        'warning',
        'La fecha de inicio no puede ser mayor a la fecha de fin',
      );
      return false;
    }
    return true;
  };

  const triggerSearch = async () => {
    setLoading(true);
    if (!validateDates()) {
      setLoading(false);
      return;
    }
    const query = generateQuery();
    const response = await getAccruingReport(dispatch, query);
    setData(response.data);
    setHeaders(response.headers);
    setDailyLinesByDocumentId(response.dailyLinesByDocumentId);
    setLoading(false);
  };

  const setUp = async () => {
    try {
      setLoading(true);
      await triggerSearch();
      setLoading(false);
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    setUp();
  }, []);

  const filters: Filter[] = [
    {
      label: 'Fecha inicio',
      type: 'date',
      value: startDate,
      setter: setStartDate,
      dateKey: 'startDate',
    },
    {
      label: 'Fecha fin',
      type: 'date',
      value: endDate,
      setter: setEndDate,
      dateKey: 'endDate',
    },
    {
      label: 'Folio',
      type: 'text',
      value: folio,
      setter: setFolio,
    },
    {
      label: 'Producto',
      type: 'select',
      value: product,
      setter: setProduct,
      options: [
        { label: 'Todos', value: 'all' },
        { label: 'Factoring', value: 'factoring' },
        { label: 'Créditos', value: 'credits' },
      ],
    },
    {
      label: 'Nombre cliente',
      type: 'autocomplete',
      value: clientName,
      key: 'clientName',
      setter: setClientName,
    },
    {
      label: 'Rut cliente',
      type: 'text',
      value: clientIdentifier,
      setter: setClientIdentifier,
    },
    {
      label: 'Nombre emisor',
      type: 'autocomplete',
      value: businessName,
      key: 'businessName',
      setter: setBusinessName,
    },
    {
      label: 'Rut emisor',
      type: 'text',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },
  ];

  const formatUTCDate = (date: Date | string): string => {
    const utcDate = new Date(new Date(date).toUTCString());
    const day = utcDate.getUTCDate().toString().padStart(2, '0');
    const month = (utcDate.getUTCMonth() + 1).toString().padStart(2, '0');
    const year = utcDate.getUTCFullYear();
    return `${day}/${month}/${year}`;
  };

  const startTitle = formatUTCDate(startDate);
  const endTitle = formatUTCDate(endDate);

  if (loading) return <CenteredCircularProgress type="layout" />;
  return (
    <div>
      <Grid container justifyContent="left">
        <HeaderWithActions
          variant="body1"
          title={`Reporte de Ingresos por devengo ${startTitle} - ${endTitle}`}
          buttons={[
            {
              name: 'Buscar',
              action: () => { },
              color: 'primary',
              customButton: (
                <GlobalSearcher
                  filters={filters}
                  triggerSearch={triggerSearch}
                  dispatch={dispatch}
                  disableSearch={loading}
                />
              ),
            },
          ]}
        />
      </Grid>
      <ReportAccruingTable
        rows={data}
        headers={headers}
        linesByDocumentId={dailyLinesByDocumentId}
        dailyLinesByDocumentId={dailyLinesByDocumentId}
        startDate={startDate}
        endDate={endDate}
      />
    </div>
  );
};

export default AccruingBook;
